import PropTypes from "prop-types";
import * as Yup from "yup";
import { openSuccessDialog } from "../../../../utils/ui-components/pop-ups/SuccessDialog";
import { Form, Formik } from "formik";
import { useTheme, Button, CircularProgress, Grid, IconButton, InputAdornment, useMediaQuery } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import { gridSpacing } from "../../../../store/constants";
import TextField from "../../../../utils/ui-components/FormsUI/TextField";
import React, { useState } from "react";
import { UserAddValues } from "./user-list";
import { UserService } from "../../../../assets/_services/user-service";
import SwitchWrapper from "../../../../utils/ui-components/FormsUI/Switch";
import ChangePassword from "./change-password";
import SelectWrapper from "../../../../utils/ui-components/FormsUI/Select";
import MultipleSelectCheckmarksWrapper from "../../../../utils/ui-components/FormsUI/MultipleSelectCheckmarks";
import { ChannelService } from "../../../../assets/_services/channel-service";

AddUser.propTypes = {
  initialItem: PropTypes.object.isRequired,
  setOpen: PropTypes.func.isRequired,
  fetchData: PropTypes.func,
  initialData: PropTypes.object,
};

function AddUser({ initialItem, setOpen, fetchData, initialData }: any) {
  const theme: any = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));
  const [isLoading, setIsLoading] = useState(false);
  const [channels, setChannels] = useState<{ value: number | string; label: string }[]>([]);
  const [passwordShow, setPasswordShow] = useState({
    password: "",
    showPassword: false,
    confirmPassword: "",
    showConfirmPassword: false,
  });

  const FORM_VALIDATION = Yup.object().shape({
    companyName: Yup.string().required("Representative name is required"),
    firstApprover: Yup.string().required("First apptover is required"),
    secondApprover: Yup.string().notRequired(),
    companyContact: Yup.string()
      .matches(/^[1-9]\d{8}$/, "Invalid contact numbers pattern detected")
      .required("Please enter the contact number"),
    username: Yup.string()
      .required("Username is required")
      .matches(/^[a-zA-Z0-9_]*$/, "Username can contain only letters, numbers, and underscores"),
    password: Yup.string()
      .max(255)
      .required("Password is required")
      .matches(/^\S*$/, "Password cannot contain spaces")
      .min(8, "Must contain at least 8 chanractres"),
    // .matches(
    //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
    //   "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
    // ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], "Your passwords do not match.")
      .required("Confirm password is required"),
    status: Yup.boolean().required("Status is required"),
    assignChannelList: Yup.array()
      .of(
        Yup.object().shape({
          value: Yup.number().required("Channel value is required"),
        })
      )
      .required("Channel is required")
      .min(1, "At least one channel is required"),
    rateCardId: Yup.number().required("Rate Card is required"),
  });

  const createUser = (itemData: UserAddValues, setSubmitting: (isSubmitting: boolean) => void) => {
    setIsLoading(true);
    let formData = {
      companyName: itemData.companyName,
      companyContact: itemData.companyContact,
      userName: itemData.username,
      password: itemData.password,
      status: itemData.status ? 1 : 0,
      assignChannelList: itemData.assignChannelList.map((item: any) => item.value),
      rateCardId: itemData.rateCardId,
      firstLevelApproval: itemData.firstApprover,
      secondLevelApproval: itemData.allowSecondApprover ? itemData.secondApprover : null,
    };
    UserService.createParentUser(formData).then((response) => {
      if (response.isSuccess) {
        openSuccessDialog("Success", `${itemData.companyName} created successfully`);
        setOpen(false);
        fetchData();
        setIsLoading(false);
      } else {
        setSubmitting(false);
        setIsLoading(false);
      }
    });
  };

  const handleClickShowPassword = () => {
    setPasswordShow({
      ...passwordShow,
      showPassword: !passwordShow.showPassword,
    });
  };

  const handleClickShowConfirmPassword = () => {
    setPasswordShow({
      ...passwordShow,
      showConfirmPassword: !passwordShow.showConfirmPassword,
    });
  };

  const getAllFlashAndSmsChannels = (id: number | string) => {
    const values = {
      rateCardId: id, // ratecard id
      channelType: [1, 2], // get only flash (1) and bulk sms (2) channels
    };
    ChannelService.getRateCardChannelList(values).then((response) => {
      if (response.isSuccess) {
        setChannels(
          response.data.data.map((item: any) => {
            return {
              label: `${item.channel_name} ( ${
                Number(item.channel_type) === 0
                  ? "API"
                  : Number(item.channel_type) === 1
                  ? "Flash"
                  : Number(item.channel_type) === 2
                  ? "Bulk Sms"
                  : "-"
              } )`,
              value: item.id,
            };
          })
        );
      }
    });
  };

  return (
    <Grid container justifyContent={"center"}>
      <Grid item xs={12}>
        <Formik
          validateOnMount={true}
          initialValues={{
            ...initialItem,
          }}
          enableReinitialize
          validationSchema={FORM_VALIDATION}
          onSubmit={(values: UserAddValues, { setSubmitting }) => {
            createUser(values, setSubmitting);
          }}
        >
          {({ values, dirty, isSubmitting, isValid, setFieldValue, errors }) => (
            <Form>
              <Grid container columnSpacing={gridSpacing}>
                <Grid item xs={12} md={12}>
                  <TextField required label="Company Name" name="companyName" type="text" />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <SelectWrapper
                    label="Rate Card"
                    name="rateCardId"
                    options={initialData.rateCards}
                    customHandleChange={(e: any) => {
                      setFieldValue("rateCardId", e.target.value);
                      setFieldValue("assignChannelList", []);
                      getAllFlashAndSmsChannels(e.target.value);
                    }}
                  />
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <MultipleSelectCheckmarksWrapper
                    name="assignChannelList"
                    label="Assign Channels"
                    options={channels}
                    disabled={!values.rateCardId}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    placeholder="Company Contact Number"
                    name="companyContact"
                    type="tel"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">+94 |</InputAdornment>,
                    }}
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, "");
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let mobileNumber = e.target.value;
                      if (mobileNumber.length === 10 && mobileNumber.startsWith("0")) {
                        setFieldValue("companyContact", mobileNumber.slice(1));
                      } else if (mobileNumber.length === 11 && mobileNumber.startsWith("94")) {
                        setFieldValue("companyContact", mobileNumber.slice(2));
                      } else {
                        setFieldValue("companyContact", mobileNumber);
                      }
                    }}
                    mataprops={initialItem.id ? errors.companyContact : null}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <SelectWrapper
                    label="First Approver"
                    name="firstApprover"
                    options={initialData.firstApprovers}
                    customHandleChange={(e: any) => {}}
                  />
                </Grid>
                <Grid item xs={12}>
                  <SwitchWrapper
                    name="allowSecondApprover"
                    label={values.allowSecondApprover ? "Enable Second Level Approver" : "Disabled Second Level Approver"}
                    style={{ co: "green" }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <SelectWrapper
                    label="Second Approver"
                    name="secondApprover"
                    options={initialData.secondApprovers}
                    customHandleChange={(e: any) => {}}
                    disabled={!values.allowSecondApprover}
                    required={values.allowSecondApprover}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    type={"text"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                    name="username"
                    placeholder="Username"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    type={passwordShow.showPassword ? "text" : "password"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} edge="end">
                            {passwordShow.showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    name="password"
                    // label="Password"
                    placeholder="Password"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    type={passwordShow.showConfirmPassword ? "text" : "password"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton aria-label="toggle password visibility" onClick={handleClickShowConfirmPassword} edge="end">
                            {passwordShow.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    name="confirmPassword"
                    // label="Confirm Password"
                    placeholder="Confirm Password"
                    required
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <SwitchWrapper name="status" label={values.status ? "ACTIVE" : "INACTIVE"} style={{ co: "green" }} />
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      py: 2,
                    }}
                  >
                    {!matchDownSM && (
                      <Button
                        color="primary"
                        variant="contained"
                        type="button"
                        onClick={() => {
                          setOpen(false);
                        }}
                        sx={{ mr: 1 }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      color="primary"
                      variant="contained"
                      type="submit"
                      disabled={!dirty || isSubmitting || !isValid}
                      startIcon={isLoading && <CircularProgress size={"20px"} sx={{ color: "gray" }} />}
                    >
                      Create User
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
      {initialItem?.id && (
        <Grid item xs={12}>
          <ChangePassword fetchData={fetchData} setOpen={setOpen} initialItem={{ password: "", confirmPassword: "", userId: initialItem.id }} />
        </Grid>
      )}
    </Grid>
  );
}

export default AddUser;
